import * as React from 'react';
import { Flex, Box } from 'grid-styled';

import { List, ListItem, makeSelectable } from 'material-ui/List';
import Divider from 'material-ui/Divider';
import ArrowDown from 'material-ui/svg-icons/navigation/arrow-drop-down';
import ArrowUp from 'material-ui/svg-icons/navigation/arrow-drop-up';

import FooterLink from './FooterMainLink';
import NewsletterForm from './NewsletterForm/NewsletterForm';

import { mobile } from '../../../utils/deviceWidths';

import hopeTheme from '../../../styles/themes/hopeTheme';
import './Footer.scss';

import {
  aboutLinks,
  quickLinks,
  shopLinks,
  socialMediaLinks,
  viaLinks,
} from './FooterData';

const SelectableList = makeSelectable(List);

interface FooterProps {
  handleRouteStaticRoute: (
    value: string,
    category: string | null,
    hasHash: boolean,
  ) => void;
  containerWidth: number;
  shopNavOpen: boolean;
  // `location` is parsed incorretly by the typescript parser because window.location
  location: string; // eslint-disable-line no-restricted-globals
}

interface FooterState {
  listAbout: boolean;
  listHelp: boolean;
  listShop: boolean;
  listVia: boolean;
}

class Footer extends React.Component<FooterProps, FooterState> {
  constructor(props: FooterProps) {
    super(props);

    this.state = {
      listAbout: false,
      listHelp: false,
      listShop: false,
      listVia: false,
    };
  }

  handleUrlPrivacy = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    const { handleRouteStaticRoute } = this.props;
    const urlPrivacy = '/privacy-policy';
    handleRouteStaticRoute(urlPrivacy, null, true);
  };

  toggleAbout = () => {
    this.setState(prevState => {
      return {
        listAbout: !prevState.listAbout,
        listHelp: false,
        listShop: false,
        listVia: false,
      };
    });
  };

  toggleHelp = () => {
    this.setState(prevState => {
      return {
        listAbout: false,
        listHelp: !prevState.listHelp,
        listShop: false,
        listVia: false,
      };
    });
  };

  toggleShop = () => {
    this.setState(prevState => {
      return {
        listAbout: false,
        listHelp: false,
        listShop: !prevState.listShop,
        listVia: false,
      };
    });
  };

  toggleVia = () => {
    this.setState(prevState => {
      return {
        listAbout: false,
        listHelp: false,
        listShop: false,
        listVia: !prevState.listVia,
      };
    });
  };

  handleMobileRouteChange = (
    e: React.MouseEvent<HTMLElement>,
    value: string,
  ) => {
    if (!value) return;
    e.preventDefault();
    const { handleRouteStaticRoute } = this.props;
    handleRouteStaticRoute(value, null, true);
  };

  render() {
    const {
      location,
      shopNavOpen,
      containerWidth,
      handleRouteStaticRoute,
      environment,
      flags,
    } = this.props;

    const { listAbout, listHelp, listShop, listVia } = this.state;
    const isShop = Array.isArray(location.match('shop'));

    const shopNavWidth = 250;
    const width = !isShop
      ? containerWidth // there is an overlay when there isn't a shop, so no need to recalculate width
      : shopNavOpen
        ? containerWidth - shopNavWidth
        : containerWidth;

    const isTablet = width <= 1440; // 1440 matches mockup better than `tablet`
    const isMobile = width <= mobile;
    const isColumn = isTablet;

    const padding = `80px 30px`;

    const style = {
      background: hopeTheme.hopeLightBlack,
      color: '#fff',
    };

    const ulStyle = {
      fontFamily: hopeTheme.headerBold,
      fontSize: '16px',
      fontWeight: 300,
      letterSpacing: '1px',
      listStyle: 'none',
      padding: 0,
    } as React.CSSProperties;

    const liStyle = {
      marginBottom: '8px',
    };

    const expandableListStyle = {
      marginTop: isMobile ? '50px' : '100px',
      width: '100%',
    };

    const listItemStyle = {
      background: 'transparent',
      color: '#fff',
      fontFamily: hopeTheme.headerBold,
      fontSize: '14px',
    };

    const listItemInnerDivStyle = {
      color: '#fff',
    };

    const linkHeaderStyle = {
      fontFamily: hopeTheme.headerBold,
      fontSize: '14px',
      fontWeight: 600,
      letterSpacing: '2px',
      marginBottom: '20px',
      marginTop: 0,
    } as React.CSSProperties;

    const signupStyle = {
      fontFamily: hopeTheme.headerBold,
      fontSize: isColumn ? '36px' : '48px',
      fontWeight: 300,
    };

    const signupBoxStyle = {
      maxWidth: 400,
      width: '100%',
    };

    const boxStyle = {
      maxWidth: isColumn ? 400 : '100%',
      width: isColumn ? '100%' : '100%',
    };

    const dividerStyle = {
      fontFamily: hopeTheme.hopeTextGrey,
    };

    const spanStyle = {
      fontSize: '12px',
      letterSpacing: '1px',
      margin: 10,
    };

    const oneSpan = { fontWeight: 600 } as React.CSSProperties;
    const hopeSpan = { fontWeight: 300 } as React.CSSProperties;

    const shopText = 'SHOP';
    const supportText = 'SUPPORT';
    const aboutText = 'ABOUT';
    const communityText = 'COMMUNITY';

    const desktopMarginLeft = 70;

    return (
      <footer>
        <Flex column style={style} justify="center" align="center" p={padding}>
          <Flex
            column={isColumn}
            justify="center"
            align={isColumn ? 'center' : 'flex-start'}
          >
            <Flex column className="Footer-signup" style={signupBoxStyle}>
              <Box mb={10}>
                {socialMediaLinks.map(link => {
                  return (
                    <span className="Footer-socialMediaIcon" key={link.imgAlt}>
                      <a
                        href={link.url}
                        rel="noopener noreferrer"
                        target="_blank"
                      >
                        <img alt={link.imgAlt} src={link.iconUrl} />
                      </a>
                    </span>
                  );
                })}
              </Box>

              <Box my={10} style={signupStyle}>
                <span style={{ display: 'inline-block' }}>Get on</span>{' '}
                <span
                  style={{ display: 'inline-block' }}
                  className="semiBold-text"
                >
                  the wine list!
                </span>
              </Box>

              <Box mt={30}>
                <NewsletterForm
                  containerWidth={containerWidth}
                  environment={environment}
                  multiRow={isMobile}
                />
              </Box>
            </Flex>

            {isColumn ? (
              <Flex style={boxStyle}>
                <SelectableList
                  style={expandableListStyle}
                  onChange={this.handleMobileRouteChange}
                >
                  {/* <FooterListItem
                    onNestedListToggle={this.toggleShop}
                    listStateIsOpen={listShop}
                    itemText={shopText}
                    nestedItems={shopLinks}
                  /> */}
                  <ListItem
                    disableTouchRipple
                    hoverColor="none"
                    onNestedListToggle={this.toggleShop}
                    open={listShop}
                    style={listItemStyle}
                    primaryText={shopText}
                    primaryTogglesNestedList
                    rightIcon={
                      listShop ? (
                        <ArrowUp color="#fff" />
                      ) : (
                          <ArrowDown color="#fff" />
                        )
                    }
                    nestedItems={shopLinks.map(link => {
                      return (
                        <ListItem
                          disableTouchRipple
                          hoverColor="none"
                          innerDivStyle={listItemInnerDivStyle}
                          key={link.text}
                          value={link.url}
                          primaryText={link.text}
                        />
                      );
                    })}
                  />
                  <Divider style={dividerStyle} />
                  <ListItem
                    disableTouchRipple
                    hoverColor="none"
                    onNestedListToggle={this.toggleHelp}
                    open={listHelp}
                    style={listItemStyle}
                    primaryText={supportText}
                    primaryTogglesNestedList
                    rightIcon={
                      listHelp ? (
                        <ArrowUp color="#fff" />
                      ) : (
                          <ArrowDown color="#fff" />
                        )
                    }
                    nestedItems={quickLinks.map(link => {
                      return (
                        <ListItem
                          disableTouchRipple
                          hoverColor="none"
                          innerDivStyle={listItemInnerDivStyle}
                          key={link.text}
                          value={link.url}
                          primaryText={link.text}
                        />
                      );
                    })}
                  />
                  <Divider style={dividerStyle} />
                  <ListItem
                    disableTouchRipple
                    hoverColor="none"
                    onNestedListToggle={this.toggleAbout}
                    style={listItemStyle}
                    open={listAbout}
                    primaryText={aboutText}
                    primaryTogglesNestedList
                    rightIcon={
                      listAbout ? (
                        <ArrowUp color="#fff" />
                      ) : (
                          <ArrowDown color="#fff" />
                        )
                    }
                    nestedItems={aboutLinks.map(link => {
                      return (
                        <ListItem
                          disableTouchRipple
                          hoverColor="none"
                          innerDivStyle={listItemInnerDivStyle}
                          key={link.text}
                          value={link.url}
                          primaryText={link.text}
                        />
                      );
                    })}
                  />
                  <Divider style={dividerStyle} />
                  <ListItem
                    disableTouchRipple
                    hoverColor="none"
                    onNestedListToggle={this.toggleVia}
                    style={listItemStyle}
                    open={listVia}
                    primaryText={communityText}
                    primaryTogglesNestedList
                    rightIcon={
                      listVia ? (
                        <ArrowUp color="#fff" />
                      ) : (
                          <ArrowDown color="#fff" />
                        )
                    }
                    nestedItems={viaLinks.map(link => {
                      return (
                        <ListItem
                          disableTouchRipple
                          hoverColor="none"
                          innerDivStyle={listItemInnerDivStyle}
                          key={link.text}
                          value={link.url}
                          primaryText={link.text}
                        />
                      );
                    })}
                  />
                  <Divider style={dividerStyle} />
                </SelectableList>
              </Flex>
            ) : (
                <Flex style={boxStyle} justify="space-between">
                  <Box ml={desktopMarginLeft}>
                    <section>
                      <h4 style={linkHeaderStyle}>
                        <span>{shopText}</span>
                      </h4>

                      <ul style={ulStyle}>
                        {shopLinks.map(link => {
                          return (
                            <li key={link.text} style={liStyle}>
                              <FooterLink
                                url={link.url}
                                linkName={link.text}
                                handleRouteStaticRoute={handleRouteStaticRoute}
                              />
                            </li>
                          );
                        })}
                      </ul>
                    </section>
                  </Box>

                  <Box ml={desktopMarginLeft}>
                    <section>
                      <h4 style={linkHeaderStyle}>
                        <span>{supportText}</span>
                      </h4>

                      <ul style={ulStyle}>
                        {quickLinks.map(link => {
                          return (
                            <li key={link.text} style={liStyle}>
                              <FooterLink
                                url={link.url}
                                linkName={link.text}
                                handleRouteStaticRoute={handleRouteStaticRoute}
                              />
                            </li>
                          );
                        })}
                      </ul>
                    </section>
                  </Box>

                  <Box ml={desktopMarginLeft}>
                    <section>
                      <h4 style={linkHeaderStyle}>
                        <span>{aboutText}</span>
                      </h4>

                      <ul style={ulStyle}>
                        {aboutLinks.map(link => {
                          return (
                            <li key={link.text} style={liStyle}>
                              <FooterLink
                                isOutbound={link.isOutbound}
                                url={link.url}
                                linkName={link.text}
                                handleRouteStaticRoute={handleRouteStaticRoute}
                              />
                            </li>
                          );
                        })}
                      </ul>
                    </section>
                  </Box>

                  <Box ml={desktopMarginLeft}>
                    <section>
                      <h4 style={linkHeaderStyle}>
                        <span>{communityText}</span>
                      </h4>

                      <ul style={ulStyle}>
                        {viaLinks.map(link => {
                          return (
                            <li key={link.text} style={liStyle}>
                              <FooterLink
                                isOutbound={link.isOutbound}
                                url={link.url}
                                linkName={link.text}
                                handleRouteStaticRoute={handleRouteStaticRoute}
                              />
                            </li>
                          );
                        })}
                      </ul>
                    </section>
                  </Box>
                </Flex>
              )}
          </Flex>

          <Box mt={106}>
            <span style={spanStyle}>
              ©<span style={oneSpan}>ONE</span>
              <span style={hopeSpan}>HOPE</span>
            </span>
            <span style={spanStyle}>
              <a
                className="Footer-privacyPolicy"
                href="/privacy-policy"
                onClick={this.handleUrlPrivacy}
              >
                Privacy Policy
              </a>
            </span>
          </Box>
        </Flex>
      </footer>
    );
  }
}

export default Footer;
